import { jsx as _jsx } from "react/jsx-runtime";
/**
 * L6.3a — LocalizationProvider.
 *
 * Pure wrapper around LocalizationContext. Accepts a possibly-null
 * partial LocalizationProfile (the recipient-led UI chain from
 * switch-context) and an optional communicationLocalization (the
 * workspace-led comms chain, stored but not consumed by UI formatters).
 *
 * Behavior:
 *   - Never mutates input.
 *   - Falls back to platform defaults per field when fields are missing,
 *     null, or empty string. Apps that want SDK-style switch-context
 *     normalization should call extractLocalizationFromSwitchContext
 *     externally and pass its `localization` / `communicationLocalization`
 *     into this provider — the provider does not parse switch-context
 *     responses directly.
 *   - No API calls, no side effects, no persistence.
 *   - Re-renders children only when the resolved profile or
 *     communicationLocalization reference changes.
 *
 * See docs/LOCALIZATION-ARCHITECTURE.md §9.
 */
import { useMemo } from 'react';
import { PLATFORM_LANGUAGE_DEFAULT, PLATFORM_LOCALE_DEFAULT, PLATFORM_REGION_DEFAULT, PLATFORM_TIMEZONE_DEFAULT, PLATFORM_CURRENCY_DEFAULT, } from '@roi-pros/core/localization';
import { LocalizationContext, } from './LocalizationContext';
/**
 * Treats `null` / `undefined` / `''` as absent and returns the platform
 * default for that field. Pure — does not mutate input. Internal to the
 * provider; consumers should not depend on this surface.
 */
function fillProfile(input) {
    const nonEmpty = (v, fallback) => v === null || v === undefined || v === '' ? fallback : v;
    return {
        language: nonEmpty(input?.language, PLATFORM_LANGUAGE_DEFAULT),
        locale: nonEmpty(input?.locale, PLATFORM_LOCALE_DEFAULT),
        region: nonEmpty(input?.region, PLATFORM_REGION_DEFAULT),
        timezone: nonEmpty(input?.timezone, PLATFORM_TIMEZONE_DEFAULT),
        currency: nonEmpty(input?.currency, PLATFORM_CURRENCY_DEFAULT),
    };
}
export function LocalizationProvider({ profile, communicationLocalization, children, }) {
    const value = useMemo(() => {
        const resolved = fillProfile(profile);
        const comm = communicationLocalization
            ? fillProfile(communicationLocalization)
            : null;
        return {
            localization: resolved,
            language: resolved.language,
            locale: resolved.locale,
            region: resolved.region,
            timezone: resolved.timezone,
            currency: resolved.currency,
            communicationLocalization: comm,
        };
    }, [profile, communicationLocalization]);
    return (_jsx(LocalizationContext.Provider, { value: value, children: children }));
}
//# sourceMappingURL=LocalizationProvider.js.map